import SelectorItemBox from '@/experiments/product-select-v3/SelectorItemBox';
import {getProductSelection, selectOption} from '@/product/product-selection';
import {getVariantByOption} from '@/product/product-selection/get-variant-by-option';
import {ProductOption} from '@/product/types';
import {handleize} from '@/utilities/handleize';
import classNames from 'classnames';

export default VariantSelector;

/**
 * Renders a group of variant buttons based on the provided options and settings.
 * Uses the provided option settings to determine the color and description of each option.
 * The currently selected option is highlighted.
 *
 */
function VariantSelector({option, foldOptions = false}: VariantSelectorProps) {
  const {product, selectedVariant} = getProductSelection();

  const selectedValue =
    selectedVariant?.selectedOptions.find(({name}) => name === option.name)
      ?.value ?? '';
  const description = selectedVariant?.description ?? '';
  const optionValues = option.values;

  const handleClickOption = (value: string) => {
    selectOption({...option, value});
  };

  if (!product) {
    return null;
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-y-2 md:gap-y-4">
        <div className="flex items-center">
          <div
            className={classNames('grid h-max w-full gap-1.5 md:gap-y-3', {
              'grid-cols-1': foldOptions,
              'grid-cols-2': !foldOptions,
            })}
          >
            {optionValues.map((value) => {
              const variant = getVariantByOption(
                product.variants,
                option.name,
                value
              );

              if (!variant) {
                return null;
              }

              const {accentColor} = variant;
              const color = accentColor ?? ';';

              return (
                <SelectorItemBox
                  color={handleize(color)}
                  isSelected={selectedValue === value}
                  key={value}
                  dataTestid={value}
                  label={
                    <span
                      className={classNames(
                        'w-full whitespace-nowrap text-[15px] font-normal ',
                        {
                          'text-left': !foldOptions,
                          '-ml-4 text-center': foldOptions,
                        }
                      )}
                    >
                      {value}
                    </span>
                  }
                  onClick={() => handleClickOption(value)}
                />
              );
            })}
          </div>
        </div>
        {description && (
          <p className="mb-0.5 self-center px-3 text-center text-sm text-dark-blue-gray md:text-base">
            {description}
          </p>
        )}
      </div>
    </>
  );
}

export type VariantSelectorProps = {
  foldOptions?: boolean;
  option: ProductOption;
};
